import { useEffect, useRef } from 'react';

/**
 * ByteStream — the cinematic background loop in the Open section.
 *
 * Procedural bytes drift right-to-left across the canvas, get squeezed
 * through an invisible engine column near the page center, and exit as
 * shorter "match" tokens (in coral). Runs forever at 60fps. Pure canvas;
 * no DOM nodes per byte.
 *
 * Pauses when the tab is hidden and when prefers-reduced-motion is set.
 */
type Particle = {
  x: number;
  y: number;
  vx: number;
  /** 0 = literal byte, 1 = match token, 0..1 mid-transition */
  morph: number;
  hex: string;
  width: number;
  born: number;
  life: number;
};

const HEX_CHARS = '0123456789ABCDEF';
function rngHex(): string {
  return HEX_CHARS[Math.floor(Math.random() * 16)] + HEX_CHARS[Math.floor(Math.random() * 16)];
}

export function ByteStream() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let engineX = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
      engineX = w * 0.5;
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    /** ── Theme-aware colors. Read CSS vars at paint time so dark/light
     *     theme switch is immediate without rebuilding the canvas. */
    const readColors = () => {
      const cs = getComputedStyle(document.documentElement);
      return {
        textDim: cs.getPropertyValue('--text-3').trim() || 'oklch(0.62 0.008 36)',
        accent: cs.getPropertyValue('--accent').trim() || 'oklch(0.66 0.18 36)',
        accentSoft: cs.getPropertyValue('--accent-soft').trim() || 'oklch(0.78 0.16 36)',
      };
    };

    const particles: Particle[] = [];
    const maxParticles = 90;

    const spawn = (now: number) => {
      const yLanes = 14;
      const lane = Math.floor(Math.random() * yLanes);
      const y = (h / (yLanes + 1)) * (lane + 1);
      const speed = 28 + Math.random() * 22; // px/s
      particles.push({
        x: w + 40,
        y,
        vx: -speed,
        morph: 0,
        hex: rngHex(),
        width: 26,
        born: now,
        life: 0,
      });
    };

    let lastSpawn = 0;
    let raf = 0;
    let running = true;
    let lastTick = performance.now();

    const onVis = () => {
      running = !document.hidden;
      if (running) {
        lastTick = performance.now();
        raf = requestAnimationFrame(tick);
      } else {
        cancelAnimationFrame(raf);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - lastTick) / 1000); // clamp for tab-blur jumps
      lastTick = now;

      // Spawn cadence ~ every 80–140 ms
      if (now - lastSpawn > 80 + Math.random() * 60 && particles.length < maxParticles) {
        spawn(now);
        lastSpawn = now;
      }

      ctx.clearRect(0, 0, w, h);

      const { textDim, accent, accentSoft } = readColors();

      ctx.font = '500 11px "Geist Mono", monospace';
      ctx.textBaseline = 'middle';

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life += dt;
        p.x += p.vx * dt;

        // Squeeze morph: when particle crosses the engine column, shrink
        // and shift to match-token (coral) over ~0.4s.
        const intoEngine = Math.max(0, Math.min(1, (engineX + 80 - p.x) / 160));
        p.morph = intoEngine;

        const alpha = p.x > w - 40 ? (w - p.x) / 40 : p.x < 40 ? p.x / 40 : 1;
        if (alpha <= 0) {
          particles.splice(i, 1);
          continue;
        }

        // Draw rounded byte cell
        const cellW = p.width - p.morph * 6;
        const cellH = 20 - p.morph * 2;
        const x0 = p.x - cellW / 2;
        const y0 = p.y - cellH / 2;
        const r = 5;

        ctx.globalAlpha = alpha * (0.45 + 0.55 * (1 - Math.abs(0.5 - intoEngine) * 2));

        // Fill
        if (p.morph > 0.5) {
          ctx.fillStyle = withAlpha(accent, 0.12);
          ctx.strokeStyle = withAlpha(accent, 0.4);
        } else {
          ctx.fillStyle = withAlpha(textDim, 0.06);
          ctx.strokeStyle = withAlpha(textDim, 0.18);
        }

        roundRect(ctx, x0, y0, cellW, cellH, r);
        ctx.fill();
        ctx.stroke();

        // Text
        ctx.fillStyle = p.morph > 0.5 ? accent : textDim;
        const label = p.morph > 0.5 ? '<' + Math.floor(p.morph * 7 + 2) + ',' + Math.floor(p.morph * 9 + 3) + '>' : p.hex;
        ctx.fillText(label, p.x - cellW / 2 + 6, p.y);

        if (p.x < -60) {
          particles.splice(i, 1);
        }
      }

      // Draw the engine column (very subtle vertical glow)
      const gradient = ctx.createLinearGradient(engineX - 80, 0, engineX + 80, 0);
      gradient.addColorStop(0, withAlpha(accentSoft, 0));
      gradient.addColorStop(0.5, withAlpha(accentSoft, 0.05));
      gradient.addColorStop(1, withAlpha(accentSoft, 0));
      ctx.globalAlpha = 1;
      ctx.fillStyle = gradient;
      ctx.fillRect(engineX - 80, 0, 160, h);

      if (running) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
}

/* ── helpers ─────────────────────────────────────────────────────────── */

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Tack an alpha value onto an OKLCH or hex color string.
 * Accepts: "oklch(0.66 0.18 36)" → "oklch(0.66 0.18 36 / 0.4)"
 *           "#E8654A"            → "rgb(232 101 74 / 0.4)" (fallback)
 */
function withAlpha(color: string, a: number): string {
  const c = color.trim();
  if (c.startsWith('oklch(')) {
    const inner = c.slice(6, -1).trim();
    return `oklch(${inner} / ${a})`;
  }
  if (c.startsWith('#')) {
    const hex = c.slice(1);
    const full = hex.length === 3 ? hex.split('').map((x) => x + x).join('') : hex;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgb(${r} ${g} ${b} / ${a})`;
  }
  return c;
}
